export interface LanguageEntry {
    languages1?: string | null;
}

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const NO_LANGUAGE_INFORMATION_KEY = 'NoLanguageInformation';
const LANGUAGES_SPOKEN_KEY = 'LanguagesSpoken';

export const getLanguageCodes = (languages: LanguageEntry[] | null | undefined): string[] =>
    (languages ?? []).map((lang) => lang?.languages1?.trim()).filter((code): code is string => Boolean(code));

const isResolved = (name: string | undefined, code: string): name is string =>
    Boolean(name) && name!.toLowerCase() !== code.toLowerCase();

// `languages1` is a flag/country code, so fall back to the region's likely language (`ir` → `fa`).
const inferLanguageFromRegion = (code: string): string | null => {
    try {
        const region = code.toUpperCase();
        const maximized = new Intl.Locale(`und-${region}`).maximize();
        if (maximized.region !== region) return null;
        return maximized.language && maximized.language !== 'und' ? maximized.language : null;
    } catch {
        return null;
    }
};

const resolveLanguageName = (rawCode: string, displayNames: Intl.DisplayNames | undefined): string | null => {
    const code = rawCode.trim().toLowerCase();
    if (!code || !displayNames) return code || null;

    const safeOf = (value: string): string | undefined => {
        try {
            return displayNames.of(value);
        } catch {
            return undefined;
        }
    };

    // Region/country code first, since `languages1` is a flag code (`ch` → German, not Chamorro).
    const inferred = inferLanguageFromRegion(code);
    if (inferred) {
        const inferredName = safeOf(inferred);
        if (isResolved(inferredName, inferred)) return inferredName;
    }

    // Fall back to interpreting the value as a language code (`en` → English).
    const name = safeOf(code);
    if (isResolved(name, code)) return name;

    return null;
};

export const getLanguageDisplayNames = (codes: string[], locale: string): string[] => {
    if (codes.length === 0) return [];

    let displayNames: Intl.DisplayNames | undefined;
    try {
        displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    } catch {
        return codes;
    }

    return codes.map((code) => resolveLanguageName(code, displayNames)).filter((name): name is string => Boolean(name));
};

export const joinLanguageNames = (names: string[], locale: string): string => {
    // `Intl.ListFormat` isn't in the project's TS lib target yet, though it's supported at runtime.
    type ListFormatCtorType = new (
        locales?: string | string[],
        options?: { style?: string; type?: string }
    ) => { format: (items: string[]) => string };
    const ListFormatCtor = (Intl as unknown as { ListFormat?: ListFormatCtorType }).ListFormat;
    try {
        if (!ListFormatCtor) return names.join(', ');
        return new ListFormatCtor(locale, { style: 'long', type: 'conjunction' }).format(names);
    } catch {
        return names.join(', ');
    }
};

export const getSpokenLanguages = (languages: LanguageEntry[] | null | undefined, locale: string): string | null => {
    const names = getLanguageDisplayNames(getLanguageCodes(languages), locale);
    if (names.length === 0) return null;

    return joinLanguageNames(names, locale);
};

export const formatSpokenLanguages = (
    languages: LanguageEntry[] | null | undefined,
    locale: string,
    t: TranslateFn
): string => {
    const spoken = getSpokenLanguages(languages, locale);
    if (!spoken) return t(NO_LANGUAGE_INFORMATION_KEY);

    return t(LANGUAGES_SPOKEN_KEY, { languages: spoken });
};
